import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';

import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { cssClass, flexColumnStyle } from '~src/style';
import { applyColor } from '~src/style/ColorService';

const cssPrefix = 'ListDetail';

export namespace ListDetailStyles {
  /** Transition duration for panel animations in seconds */
  export const TRANSITION_DURATION = 0.25;

  /** Divider width */
  export const DividerWidth = 4;

  /** Main container for the list-detail layout */
  export const LayoutContainer = cssClass(cssPrefix, 'layout-container', {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
  });

  /** Container for list and detail panels (below toolbar) */
  export const PanelsContainer = cssClass(cssPrefix, 'panels-container', {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
  });

  /** List panel (left side) */
  export const ListPanel = cssClass(cssPrefix, 'list-panel', {
    width: '30%',
    minWidth: 75,
    maxWidth: 420,
    height: '100%',
    overflow: 'hidden',
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.default, TabStyles.Colors.White),
    borderRight: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles.Colors.L3)}`,
    transition: `width ${TRANSITION_DURATION}s ease-in-out`,
    flexShrink: 0,
  });

  /** List panel when collapsed */
  export const ListPanelCollapsed = cssClass(cssPrefix, 'list-panel-collapsed', {
    width: 0,
    overflow: 'hidden',
    borderRight: 'none',
  });

  /** Detail panel (right side) */
  export const DetailPanel = cssClass(cssPrefix, 'detail-panel', {
    flex: 1,
    height: '100%',
    overflow: 'hidden',
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.default, TabStyles.Colors.White),
    transition: `margin-left ${TRANSITION_DURATION}s ease-in-out`,
  });

  /** Resizable divider between panels */
  export const Divider = cssClass(cssPrefix, 'divider', {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: DividerWidth,
    cursor: 'col-resize',
    zIndex: 10,
    transition: `left ${TRANSITION_DURATION}s ease-in-out`,
    border: 'none',
    background: 'transparent',
    padding: 0,

    $nest: {
      '&:focus': {
        outline: 'none',
      }
    }
  });

  /** Divider when actively resizing */
  export const DividerResizing = cssClass(cssPrefix, 'divider-resizing', {
    transition: 'none',
  });

  /** Content wrapper for list panel (top/bottom padding only; content controls horizontal spacing) */
  export const ListContent = cssClass(cssPrefix, 'list-content', flexColumnStyle, {
    height: '100%',
    paddingTop: TabStyles.Spacing.LargeSpace,
    paddingBottom: TabStyles.Spacing.LargeSpace,
    overflow: 'auto',
  });

  /** Content wrapper for detail panel (no padding; content controls its own spacing) */
  export const DetailContent = cssClass(cssPrefix, 'detail-content', flexColumnStyle, {
    height: '100%',
    overflow: 'auto',
  });

  /** Empty state message */
  export const EmptyState = cssClass(cssPrefix, 'empty-state', {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Colors.F7),
    textAlign: 'center',
    padding: TabStyles.Spacing.ExtraLargeSpace,
  });

  /** Loading state */
  export const LoadingState = cssClass(cssPrefix, 'loading-state', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  });

  /** Toolbar container */
  export const ToolbarContainer = cssClass(cssPrefix, 'toolbar-container', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px 16px',
    borderBottom: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, CommonStyles.Colors.DividerLineColor)}`,
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.default, CommonStyles.Colors.White),
  });

  /** Toolbar controls group */
  export const ToolbarControlsGroup = cssClass(cssPrefix, 'toolbar-controls-group', {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  });
}
